import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useProject } from '../contexts/ProjectContext'
import { getAppConfig } from '../services/projectService'
import { isCostManagementProject, PROJECT_IDS } from '../config/projects'
import DashboardNav from '../components/DashboardNav'
import ApprovalSummary from '../components/ApprovalSummary'
import Alert from '../components/Alert'
import Toast from '../components/Toast'
import { useEquipmentYardAssets } from '../hooks/useEquipmentYardAssets'
import { useAssetRedirectDownload } from '../hooks/useAssetRedirectDownload'
import useFormPermissions from '../hooks/useFormPermissions'
import constructionBanner from '../assets/images/construction-banner.webp'
import '../styles/Dashboard.css';

const DashboardPage = ({ setIsSidebarOpen, projectsModalOpen, setProjectsModalOpen }) => {
  const { user, token, logout } = useAuth()
  const { selectedProject, loading: projectsLoading } = useProject()

  // Automatically fetch assets when Equipment Yard is selected
  const { assets, loading: assetsLoading, error: assetsError } = useEquipmentYardAssets(
    selectedProject?.id === PROJECT_IDS.EQUIPMENT_YARD ? selectedProject.id : null
  )

  // Configuration from backend (AWS Secrets) - for template ID
  const [config, setConfig] = useState(null)
  const [configLoading, setConfigLoading] = useState(true)
  const [configError, setConfigError] = useState(null)

  // Form permissions check (replaces old reviewer whitelist check)
  const {
    hasReviewerAccess,
    loading: permissionsLoading,
    error: permissionsError,
  } = useFormPermissions(
    selectedProject?.id,
    config?.fuelLogTemplateId,
    {
      enabled: !!(selectedProject?.id && config?.fuelLogTemplateId)
    }
  )

  // Hidden feature - Asset Redirect Download (triggered by typing "assets")
  const { downloadAssetRedirects, loading: downloadLoading } = useAssetRedirectDownload()
  const [keyBuffer, setKeyBuffer] = useState('')

  // Toast state for view-only access notification
  const [showViewOnlyToast, setShowViewOnlyToast] = useState(false)

  // Fetch configuration on mount (still needed for fuel log template ID)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await getAppConfig()
        setConfig(response.config)
      } catch (error) {
        console.error('[Dashboard] Config error:', error)
        setConfigError(error.message)
      } finally {
        setConfigLoading(false)
      }
    }

    fetchConfig()
  }, [])

  // Show toast when user has view-only access (Equipment Yard only)
  useEffect(() => {
    // Only show after data is loaded and permissions are determined
    if (selectedProject && config && !permissionsLoading && !configLoading) {
      const isEquipmentYard = selectedProject.id === PROJECT_IDS.EQUIPMENT_YARD
      // Only show for Equipment Yard with explicit view-only access
      if (isEquipmentYard && hasReviewerAccess === false) {
        setShowViewOnlyToast(true)
      } else {
        setShowViewOnlyToast(false)
      }
    } else {
      // Hide toast while loading
      setShowViewOnlyToast(false)
    }
  }, [selectedProject, config, hasReviewerAccess, permissionsLoading, configLoading])

  // Hidden feature activation - Listen for secret keyword "assets" to trigger download
  useEffect(() => {
    const SECRET_PHRASE = 'assets'

    const handleKeyDown = (e) => {
      // Ignore if typing in input, textarea, or contenteditable
      const target = e.target
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // Build key buffer with typed characters
      const newBuffer = (keyBuffer + e.key).toLowerCase()

      // Keep only the last N characters (length of secret phrase)
      const trimmedBuffer = newBuffer.slice(-SECRET_PHRASE.length)
      setKeyBuffer(trimmedBuffer)

      // Check if secret phrase is typed
      if (trimmedBuffer === SECRET_PHRASE) {
        console.log('[Dashboard] 🔓 Secret phrase detected! Triggering asset download...')

        // Trigger download if on Equipment Yard project
        if (selectedProject?.id === PROJECT_IDS.EQUIPMENT_YARD && token) {
          downloadAssetRedirects(selectedProject.id, token)
        } else if (!selectedProject || selectedProject.id !== PROJECT_IDS.EQUIPMENT_YARD) {
          console.warn('[Dashboard] Asset download only available for Equipment Yard project')
        } else if (!token) {
          console.warn('[Dashboard] No token available for download')
        }

        // Clear buffer after trigger
        setKeyBuffer('')
      }
    }

    // Add event listener - use keydown instead of keypress (keypress is deprecated)
    window.addEventListener('keydown', handleKeyDown)

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [keyBuffer, selectedProject, token, downloadAssetRedirects])

  return (
    <div className="dashboard-container">
      <DashboardNav
        user={user}
        onLogout={logout}
        setIsSidebarOpen={setIsSidebarOpen}
        projectsModalOpen={projectsModalOpen}
        setProjectsModalOpen={setProjectsModalOpen}
      />

      <main className="dashboard-main">
        {/* Welcome Banner */}
        <div className="welcome-banner">
          <div style={{ position: 'relative' }}>
            <img src={constructionBanner} alt="Construction Equipment" className="welcome-banner-bg" />
          </div>

          <div className="welcome-banner-content">
            <h1 className="welcome-title">Welcome back, {user?.name?.split(' ')[0] || 'User'}!</h1>
            <p className="welcome-subtitle">Here's what's pending your review today</p>
          </div>
        </div>

        <div className="welcome-section">

          {configLoading && (
            <p style={{ color: '#666', textAlign: 'center' }}>Loading configuration...</p>
          )}

          {configError && (
            <Alert type="error" title="Configuration Error" message={configError} />
          )}

          {/* Show loader while checking permissions */}
          {config && permissionsLoading && selectedProject && (
            <div style={{ textAlign: 'center', marginTop: '2rem', padding: '2rem' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
              <p style={{ color: '#666', fontSize: '1.1rem', fontWeight: '500' }}>
                Checking your form permissions...
              </p>
            </div>
          )}

          {/* Show permission error if any */}
          {permissionsError && selectedProject && (
            <Alert
              type="warning"
              title="Permission Check Failed"
              message={`Could not verify permissions: ${permissionsError}. Showing view-only mode.`}
            />
          )}

          {/* Show content only after permission check is complete */}
          {config && !permissionsLoading && !projectsLoading && (
            <>
              {!selectedProject ? (
                <Alert
                  type="info"
                  title="No Project Selected"
                  message="Please select a project from the dropdown above to view approvals."
                />
              ) : (
                <>
                  {/* Show view-only message if user doesn't have reviewer access */}
                  {/* Approval Summary - Shown to ALL users (action buttons will be hidden for view-only users) */}
                  <ApprovalSummary
                    projectId={selectedProject.id}
                    projectName={selectedProject.name}
                    templateId={config.fuelLogTemplateId}
                    config={config}
                    isAuthenticated={user?.authenticated}
                  />
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Toast container for view-only access notification */}
      <div className="toast-container">
        {showViewOnlyToast && (
          <Toast
            message="Some of the template forms has View-only access. Contact your project administrator"
            type="info"
            onClose={() => setShowViewOnlyToast(false)}
          />
        )}
      </div>
    </div>
  )
}

export default DashboardPage
